import React from 'react';
import { RefreshCw, BarChart3, MousePointerClick, Eye } from 'lucide-react';

const PAGE_NAMES = { '/': 'Home', '/about': 'About', '/faq': 'FAQs', '/contact': 'Contact', '/resources': 'Resources', '/terms': 'Terms & Privacy' };

function PageViewsTable({ data }) {
  const max = data[0]?.count || 1;
  return (
    <table className="admin-table">
      <thead>
        <tr>
          <th>Page</th>
          <th className="text-right">Views</th>
          <th className="bar-col"></th>
        </tr>
      </thead>
      <tbody>
        {data.map(function(p) {
          const w = Math.round((p.count / max) * 100);
          return (
            <tr key={p.page}>
              <td>{PAGE_NAMES[p.page] || p.page}</td>
              <td className="text-right fw600">{p.count}</td>
              <td>
                <div className="analytics-bar-bg">
                  <div className="analytics-bar-fill" style={{ width: w + '%' }}></div>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function ClickEventsTable({ data }) {
  const max = data[0]?.count || 1;
  return (
    <table className="admin-table">
      <thead>
        <tr>
          <th>Element</th>
          <th className="text-right">Clicks</th>
          <th className="bar-col"></th>
        </tr>
      </thead>
      <tbody>
        {data.map(function(ev) {
          const w = Math.round((ev.count / max) * 100);
          return (
            <tr key={ev.element}>
              <td>{ev.element}</td>
              <td className="text-right fw600">{ev.count}</td>
              <td>
                <div className="analytics-bar-bg">
                  <div className="analytics-bar-fill analytics-bar-accent" style={{ width: w + '%' }}></div>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function DailyChart({ data }) {
  const maxDay = Math.max(...data.map(function(v) { return v.count; }), 1);
  return (
    <div className="daily-chart" data-testid="daily-chart">
      {data.map(function(d) {
        const h = Math.max(Math.round((d.count / maxDay) * 120), 4);
        return (
          <div key={d.date} className="daily-bar-wrapper" title={d.date + ': ' + d.count + ' views'}>
            <div className="daily-bar" style={{ height: h }}></div>
            <span className="daily-label">{d.date.slice(5)}</span>
          </div>
        );
      })}
    </div>
  );
}

function AnalyticsTab({ analytics, analyticsLoading, onRefresh }) {
  if (analyticsLoading && !analytics) {
    return React.createElement('div', { className: 'loading-state' }, 'Loading analytics...');
  }
  if (!analytics) {
    return React.createElement('div', { className: 'empty-state' }, 'No analytics data yet. Browse the site to generate page views.');
  }

  return (
    <div className="analytics-tab" data-testid="analytics-tab">
      <div className="analytics-toolbar">
        <button onClick={onRefresh} disabled={analyticsLoading} className="btn-outline" data-testid="refresh-analytics-btn">
          <RefreshCw size={16} /> Refresh Analytics
        </button>
      </div>

      <div className="analytics-summary-row">
        <div className="analytics-stat-card">
          <Eye size={24} className="stat-icon" />
          <div className="stat-value" data-testid="stat-pageviews">{analytics.total_pageviews}</div>
          <div className="stat-label">Page Views</div>
        </div>
        <div className="analytics-stat-card">
          <MousePointerClick size={24} className="stat-icon" />
          <div className="stat-value" data-testid="stat-events">{analytics.total_events}</div>
          <div className="stat-label">Click Events</div>
        </div>
        <div className="analytics-stat-card">
          <BarChart3 size={24} className="stat-icon" />
          <div className="stat-value" data-testid="stat-pages-tracked">{analytics.page_views_by_page.length}</div>
          <div className="stat-label">Pages Tracked</div>
        </div>
      </div>

      <div className="analytics-tables-row">
        <div className="analytics-table-card">
          <h3>Page Views by Page</h3>
          {analytics.page_views_by_page.length > 0
            ? React.createElement(PageViewsTable, { data: analytics.page_views_by_page })
            : React.createElement('p', { className: 'empty-state' }, 'No page view data yet')
          }
        </div>
        <div className="analytics-table-card">
          <h3>Click Events</h3>
          {analytics.events_by_element.length > 0
            ? React.createElement(ClickEventsTable, { data: analytics.events_by_element })
            : React.createElement('p', { className: 'empty-state' }, 'No click data yet')
          }
        </div>
      </div>

      {analytics.daily_views.length > 0 && (
        <div className="analytics-table-card analytics-chart-card">
          <h3>Daily Page Views (Last 30 Days)</h3>
          <DailyChart data={analytics.daily_views} />
        </div>
      )}
    </div>
  );
}

export default AnalyticsTab;
